import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import Footer from "../layouts/Footer";
import avatar from "../assets/img/user-avatar.png";
import useManagePatient from "../hooks/patientDashboard/useManagePatient";
import useProfileStore from "../store/patient/profileStore";
import { toast } from "sonner";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import BtnSpinner from "../components/common/btnSpinners";
import { updateUserProfile } from "../utils/patients/updatePatient";
import Header from "../layouts/Header";
import { parseAvatar } from "../utils/chat/utils";
import ProfileModal from "../components/modals/profileModal";

const initialState = {
  title: "",
  surname: "",
  blood_group: "",
  other_names: "",
  phone_number: "",
  email: "",
  date_of_birth: new Date(),
  gender: "",
  ethnicity: "",
  occupation: "",
  address: "",
  maritalStatus: "",
  profile_pic: null,
  hmo_id: "",
  nextOfKin: {
    surname: "",
    other_names: "",
    address: "",
    gender: "",
    relationship: "",
    occupation: "",
    phoneNumber: "",
    ethnicity: "",
  },
};

const displayValue = (value, fallback = "Not provided") =>
  value === undefined || value === null || value === "" ? fallback : value;

const getPatientId = (profile) => profile?.id || profile?.legacy_patient_id || "Pending";

export default function Profile() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = React.useState(currentSkin);
  const { profile } = useProfileStore();
  const [loading, setLoading] = useState(false);
  const {
    loading: uploading,
    handleImageUpload,
    findPatient,
    resetPatientPassword,
  } = useManagePatient();
  const [showProfilePicModal, setShowProfilePicModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formState, setFormState] = useState(initialState);

  const toggleProfilePicModal = () => {
    setShowProfilePicModal((current) => !current);
  };

  const handleSave = async () => {
    if (!selectedImg) {
      toast.error("Please select picture first!");
      return;
    }

    try {
      await handleImageUpload(selectedImg);
      setShowProfilePicModal(false);
      setSelectedImg(null);
    } catch (error) {
      console.error("Error for save:", error);
    }
  };

  const switchSkin = (currentMode) => {
    if (currentMode === "dark") {
      const btnWhite = document.getElementsByClassName("btn-white");

      for (const btn of btnWhite) {
        btn.classList.add("btn-outline-primary");
        btn.classList.remove("btn-white");
      }
      return;
    }

    const btnOutlinePrimary = document.getElementsByClassName("btn-outline-primary");

    for (const btn of btnOutlinePrimary) {
      btn.classList.remove("btn-outline-primary");
      btn.classList.add("btn-white");
    }
  };

  React.useEffect(() => {
    switchSkin(skin);
  }, [skin]);

  useEffect(() => {
    if (!profile) {
      return;
    }

    setFormState({
      title: profile.title || "",
      surname: profile.surname || "",
      other_names: profile.other_names || "",
      blood_group: profile.blood_group || "",
      phone_number: profile.phone_number || "",
      email: profile.email || "",
      date_of_birth: profile.date_of_birth || new Date(),
      gender: profile.gender || "",
      ethnicity: profile.ethnicity || "",
      occupation: profile.occupation || "",
      address: profile.address || "",
      maritalStatus: profile.maritalStatus || "",
      profile_pic: profile.profile_pic || null,
      hmo_id: profile?.hmo?.id,
      nextOfKin: {
        surname: profile?.nextOfKin?.surname || "",
        other_names: profile?.nextOfKin?.other_names || "",
        address: profile?.nextOfKin?.address || "",
        gender: profile?.nextOfKin?.gender || "",
        relationship: profile?.nextOfKin?.relationship || "",
        occupation: profile?.nextOfKin?.occupation || "",
        phoneNumber: profile?.nextOfKin?.phoneNumber || "",
        ethnicity: profile?.nextOfKin?.ethnicity || "",
      },
    });
  }, [profile]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name.startsWith("nextOfKin.")) {
      const key = name.split(".")[1];
      setFormState((prevState) => ({
        ...prevState,
        nextOfKin: {
          ...prevState.nextOfKin,
          [key]: value,
        },
      }));
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const resetPassword = () => {
    if (password?.trim() !== confirmPassword?.trim()) {
      toast.error("Password mismatch!");
      return;
    }

    const payload = {
      email: profile?.email,
      password,
      repassword: confirmPassword,
      address: profile?.address,
    };

    resetPatientPassword(payload);
    setPassword("");
    setConfirmPassword("");
  };

  const handleDateChange = (date) => {
    if (date) {
      setFormState({ ...formState, date_of_birth: moment(date).format("YYYY-MM-DD") });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const res = await updateUserProfile(profile?.id, formState);

      if (res.data.success) {
        await findPatient();
        toast.success("Profile updated successfully");
      }
    } catch (error) {
      toast.error("Unable to save profile changes");
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    {
      icon: "ri-medal-2-line",
      label: "HMO",
      value: displayValue(profile?.hmo?.name),
    },
    {
      icon: "ri-user-line",
      label: "Gender",
      value: displayValue(profile?.gender),
    },
    {
      icon: "ri-user-heart-line",
      label: "Marital status",
      value: displayValue(profile?.maritalStatus),
    },
    {
      icon: "ri-briefcase-3-line",
      label: "Occupation",
      value: displayValue(profile?.occupation),
    },
  ];

  const quickFacts = [
    { label: "Patient ID", value: getPatientId(profile) },
    { label: "Blood Group", value: displayValue(profile?.blood_group, "--") },
    { label: "Genotype", value: displayValue(profile?.genotype, "--") },
    {
      label: "Next of Kin",
      value: profile?.nextOfKin
        ? `${profile?.nextOfKin?.other_names || ""} ${profile?.nextOfKin?.surname || ""}`.trim() || "Not added"
        : "Not added",
    },
  ];

  const patientFullName =
    `${profile?.title ? `${profile.title} ` : ""}${profile?.surname || ""} ${profile?.other_names || ""}`.trim() ||
    "Your Profile";
  const profileImage = profile?.profile_pic ? parseAvatar(profile.profile_pic) : avatar;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4 profile-page-shell">
        <section className="profile-hero">
          <div className="profile-hero-main">
            <button type="button" className="profile-hero-avatar" onClick={toggleProfilePicModal}>
              <img src={profileImage} alt={patientFullName} />
              <span className="profile-hero-avatar-badge">
                <i className="ri-camera-line"></i>
              </span>
            </button>

            <div className="profile-hero-copy">
              <span className="profile-hero-kicker">Patient profile</span>
              <h1>{patientFullName}</h1>
              <p>
                Keep your personal record current so appointments, billing, and care
                communication always stay accurate.
              </p>

              <div className="profile-hero-contact">
                <span>
                  <i className="ri-mail-line"></i>
                  {displayValue(profile?.email)}
                </span>
                <span>
                  <i className="ri-phone-line"></i>
                  {displayValue(profile?.phone_number)}
                </span>
                <span>
                  <i className="ri-map-pin-line"></i>
                  {displayValue(profile?.address)}
                </span>
              </div>
            </div>

            <div className="profile-hero-actions">
              <Button className="btn-deda" onClick={() => setShowProfileModal(true)}>
                View Profile Card
              </Button>
              <Button variant="" className="btn-white" onClick={toggleProfilePicModal}>
                Change Photo
              </Button>
            </div>
          </div>

          <div className="profile-hero-stats">
            {infoCards.map((item) => (
              <div className="profile-hero-stat" key={item.label}>
                <div className="profile-hero-stat-icon">
                  <i className={item.icon}></i>
                </div>
                <div>
                  <small>{item.label}</small>
                  <strong>{item.value}</strong>
                </div>
              </div>
            ))}
          </div>
        </section>

        <Row className="g-4 align-items-start">
          <Col xl={8}>
            <Card className="card-post profile-editor-card">
              <Card.Header>
                <div>
                  <Card.Title className="d-flex gap-2 align-items-center">
                    <i className="ri-edit-line"></i>
                    Update Profile
                  </Card.Title>
                  <p className="profile-card-subtext mb-0">
                    Review your personal details, contact information, and emergency contact.
                  </p>
                </div>
              </Card.Header>

              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <div className="profile-form-section">
                    <div className="profile-section-heading">
                      <h6>Basic details</h6>
                      <p>Your identity and demographic information.</p>
                    </div>

                    <div className="profile-form-grid">
                      <div className="profile-field">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Title"
                          name="title"
                          value={formState.title}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Surname</Form.Label>
                        <Form.Control
                          type="text"
                          name="surname"
                          value={formState.surname}
                          onChange={handleChange}
                          placeholder="Surname"
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Other Names</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Other names"
                          name="other_names"
                          value={formState.other_names}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Blood Group</Form.Label>
                        <Form.Control
                          type="text"
                          name="blood_group"
                          disabled
                          value={formState.blood_group}
                          onChange={handleChange}
                          placeholder="Blood group"
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Date of Birth</Form.Label>
                        <ReactDatePicker
                          selected={formState.date_of_birth ? new Date(formState.date_of_birth) : new Date()}
                          onChange={handleDateChange}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          dateFormat="dd-MM-yyyy"
                          className="single-daterange form-control"
                          placeholderText="Select date of birth"
                          maxDate={new Date()}
                          disabledKeyboardNavigation
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Gender</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Gender"
                          className="text-capitalize"
                          name="gender"
                          value={formState.gender}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="profile-form-section">
                    <div className="profile-section-heading">
                      <h6>Contact & background</h6>
                      <p>How the care team can reach and identify you.</p>
                    </div>

                    <div className="profile-form-grid">
                      <div className="profile-field">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Phone number"
                          name="phone_number"
                          value={formState.phone_number}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Email"
                          name="email"
                          value={formState.email}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Ethnicity</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ethnicity"
                          name="ethnicity"
                          value={formState.ethnicity}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Occupation</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Occupation"
                          className="text-capitalize"
                          name="occupation"
                          value={formState.occupation}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Marital Status</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Marital status"
                          name="maritalStatus"
                          value={formState.maritalStatus}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field profile-field--wide">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Address"
                          name="address"
                          value={formState.address}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="profile-form-section">
                    <div className="profile-section-heading">
                      <h6>Next of kin</h6>
                      <p>Emergency contact and support person information.</p>
                    </div>

                    <div className="profile-form-grid">
                      <div className="profile-field">
                        <Form.Label>Surname</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Surname"
                          name="nextOfKin.surname"
                          value={formState?.nextOfKin?.surname}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Other Names</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Other names"
                          name="nextOfKin.other_names"
                          value={formState?.nextOfKin?.other_names}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Gender</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Gender"
                          name="nextOfKin.gender"
                          value={formState?.nextOfKin?.gender}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Phone number"
                          name="nextOfKin.phoneNumber"
                          value={formState?.nextOfKin?.phoneNumber}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Occupation</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Occupation"
                          name="nextOfKin.occupation"
                          value={formState?.nextOfKin?.occupation}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Relationship</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Relationship"
                          name="nextOfKin.relationship"
                          value={formState?.nextOfKin?.relationship}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field profile-field--wide">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Address"
                          name="nextOfKin.address"
                          value={formState?.nextOfKin?.address}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="profile-field">
                        <Form.Label>Ethnicity</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ethnicity"
                          name="nextOfKin.ethnicity"
                          value={formState?.nextOfKin?.ethnicity}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="profile-form-actions">
                    <Button variant="primary" disabled={loading} className="text-white" type="submit">
                      {loading ? <BtnSpinner /> : "Save Changes"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={4}>
            <div className="profile-side-stack">
              <Card className="card-one profile-side-card">
                <Card.Header>
                  <div>
                    <h6 className="mb-1">Quick Snapshot</h6>
                    <p className="profile-card-subtext mb-0">A quick read of your most important profile data.</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="profile-quick-list">
                    {quickFacts.map((item) => (
                      <div className="profile-quick-item" key={item.label}>
                        <span>{item.label}</span>
                        <strong>{displayValue(item.value)}</strong>
                      </div>
                    ))}
                  </div>
                  <Button variant="" className="btn-white w-100 mt-3" onClick={() => setShowProfileModal(true)}>
                    Open Full Profile Card
                  </Button>
                </Card.Body>
              </Card>

              <Card className="card-one profile-side-card">
                <Card.Header>
                  <div>
                    <h6 className="mb-1">Security</h6>
                    <p className="profile-card-subtext mb-0">Reset your password and keep your account secure.</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="profile-security-form">
                    <div className="profile-field">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter new password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                      />
                    </div>
                    <div className="profile-field">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(event) => setConfirmPassword(event.target.value)}
                      />
                    </div>
                    <p className="profile-password-note">
                      Use a password you have not used before and keep it somewhere secure.
                    </p>
                    <Button onClick={resetPassword} variant="primary" className="text-white w-100">
                      {loading ? <BtnSpinner /> : "Update Password"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

        <Footer />
      </div>

      <ProfileModal avatar={avatar} show={showProfileModal} setShow={setShowProfileModal} />
      <ProfilePicModal
        loading={uploading}
        handleSave={handleSave}
        setSelectedImg={setSelectedImg}
        selectedImg={selectedImg}
        setShow={setShowProfilePicModal}
        show={showProfilePicModal}
      />
    </React.Fragment>
  );
}

const ProfilePicModal = ({ loading, show, selectedImg, handleSave, setSelectedImg, setShow }) => {
  const handleClose = () => setShow(false);
  const previewUrl = selectedImg ? URL.createObjectURL(selectedImg) : null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change Profile Picture</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="profile-picture-modal">
          <div className="profile-picture-preview">
            {previewUrl ? (
              <img src={previewUrl} alt="Selected preview" />
            ) : (
              <div className="profile-picture-empty">
                <i className="ri-image-add-line"></i>
                <span>Select a photo to preview it here</span>
              </div>
            )}
          </div>

          <Form.Group className="mt-3">
            <Form.Label>Upload New Picture</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(event) => setSelectedImg(event.target.files?.[0] || null)}
            />
          </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSave} className="text-white">
          {loading ? <BtnSpinner /> : "Save Changes"}
        </Button>
        <Button variant="" className="btn-white" onClick={handleClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
